
import { supabase } from "@/integrations/supabase/client";

export const getPhotoUrl = async (photoReference: string, maxWidth = 400): Promise<string> => {
  const maxRetries = 2;
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        // Exponential backoff: 500ms, 1000ms
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        console.log(`Retrying photo fetch (attempt ${attempt + 1}/${maxRetries})`);
      }
      
      const response = await supabase.functions.invoke('place-photo', {
        body: { photoReference, maxWidth }
      });

      // Check for errors in the response
      if (response.error) {
        lastError = new Error(`Edge function error: ${response.error.message}`);
        console.warn(`Photo fetch error (attempt ${attempt + 1}):`, response.error);
        continue;
      }

      // Return URL or placeholder
      if (response.data?.url) {
        return response.data.url;
      }
      
      // No URL but no error - return placeholder
      console.warn(`No photo URL returned for reference: ${photoReference.substring(0, 20)}...`);
      return "/placeholder.svg";
      
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.warn(`Photo fetch exception (attempt ${attempt + 1}):`, error);
      
      // Don't retry on network errors if this is the last attempt
      if (attempt === maxRetries - 1) {
        break;
      }
      
      continue;
    }
  }
  
  // All retries exhausted
  console.error(`Failed to fetch photo after ${maxRetries} attempts:`, lastError?.message);
  return "/placeholder.svg";
};

export const getMultiplePhotoUrls = async (
  photoReferences: Array<{ photo_reference: string }>, 
  maxWidth = 800,
  limit = 10
): Promise<string[]> => {
  const images: string[] = [];
  
  // Limit to 10 images maximum
  const referencesToFetch = photoReferences.slice(0, limit);
  
  try {
    // Fetch all photos in parallel
    const photoPromises = referencesToFetch.map(photo => 
      getPhotoUrl(photo.photo_reference, maxWidth)
    );
    
    const results = await Promise.allSettled(photoPromises);
    
    // Filter out any rejected promises and collect successful URLs
    results.forEach(result => {
      if (result.status === 'fulfilled') {
        images.push(result.value);
      }
    });
    
    return images;
  } catch (error) {
    console.error("Error fetching multiple photos:", error);
    return images;
  }
};
